// Sprint UI compaction (2026-05-11) — sidebar header compaction.
//
// What this is:
//   The small read-only pill that lives where the old "Agents" label
//   sat. Replaces the post-D-013 vestigial `ServerSelector` (which let
//   the user switch between per-VPS dashboards — a model that ceased to
//   exist when D-013 centralised the control plane on vps1.asoasis.com
//   and turned every other VPS into a runner-only host).
//
// What it shows:
//   `🟢 N runners · X% free`
//   where N is the count of runners the control plane knows about and
//   X is the WORST-case 5h cap residual across online runners — i.e.
//   the actual headroom you have if you spawn the next agent against
//   the bottleneck runner. The aggregate is conservative because the
//   scheduler does not let the founder pick which runner an agent
//   lands on; the founder cares about "how full is the system" not
//   "what's my best runner".
//
// Visual states (semantic, not vibes):
//   - green   → every runner online AND worst cap > 20% free.
//   - yellow  → mixed state: one or more offline / draining /
//                incompatible, or worst cap ≤ 20%.
//   - red     → zero runners online (cannot spawn anything).
//
// Click contract:
//   Opens the existing UsageModal (`useAppStore.setUsageModalOpen`).
//   The modal already renders the full RunnersTable that this pill
//   summarises — so the pill is summary + entry point, NOT a parallel
//   surface to maintain. Same UX pattern as Cursor's status indicators
//   (top-bar errors collapse to a count; click expands to the panel).
//
// Why a pill, not a dropdown:
//   The old `ServerSelector` was a switcher; this pill is read-only
//   information. Post-D-013 there is nothing for the user to switch
//   between — the scheduler decides routing automatically. A dropdown
//   would re-import the same trap (an interactive control that can
//   visibly do "nothing useful", inviting clicks that produce no
//   change). A pill makes the affordance honest: glanceable status,
//   one click to drill down.
//
// Hide-when-empty contract:
//   Returns null if the runners store is still pre-hydration AND has
//   zero entries. As soon as the first `/api/runners` poll lands, the
//   pill renders — typically <100 ms after the page mounts because
//   `startRunnersPolling()` fires the first hydrate synchronously
//   on mount (see `runners.store.ts::startRunnersPolling`).

import { useAppStore } from '../stores/app.store';
import { useRunnersStore } from '../stores/runners.store';
import { useRunnersUsageStore } from '../stores/runners-usage.store';
import { useTranslation } from '../i18n';
import { Badge, type BadgeColor } from './atoms/Badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { deriveRunnerLabel } from './RunnerChip';
import type { RunnerSnapshot, RunnerUsageEntry } from '../lib/api';

interface PillState {
  color: BadgeColor;
  labelKey: 'allHealthy' | 'singleRunner' | 'degraded' | 'allOffline';
  total: number;
  online: number;
  capWorst: number | null;
}

// Threshold below which the worst cap residual flips the pill to
// yellow. 20% means "you have ≤ 200 messages of headroom on the
// tightest runner before its 5h window saturates" — a useful warning
// signal without being noisy at 40-50% which is fully normal.
const CAP_WARNING_THRESHOLD_PCT = 20;

export function RunnersHealthPill() {
  const { t } = useTranslation();
  const runnersById = useRunnersStore((s) => s.runnersById);
  const loaded = useRunnersStore((s) => s.loaded);
  const usageById = useRunnersUsageStore((s) => s.entriesByRunnerId);
  const setUsageOpen = useAppStore((s) => s.setUsageModalOpen);

  const runners = Object.values(runnersById);

  // Pre-hydration AND no cached entries → render nothing. The
  // header's flex layout collapses naturally and the founder doesn't
  // see a "loading…" placeholder that flashes for 50 ms.
  if (!loaded && runners.length === 0) {
    return null;
  }

  const state = derivePillState(runners, usageById);
  const label = renderLabel(t, state);

  // Self-contained `TooltipProvider`. The Sidebar tree does not mount a
  // shared provider (DashboardLayout has none either; `ChatHeader`
  // mounts its own for `RunnerChip`). Without this wrapper Radix
  // throws "Tooltip must be wrapped in a TooltipProvider" and the
  // whole sidebar render fails — caught during the visual-parity
  // gate, not by unit tests (those wrap explicitly).
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => setUsageOpen(true)}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-caption text-foreground hover:bg-accent/40 active:bg-accent/60 transition-colors min-w-0 max-w-[180px] focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
            aria-label={t('runnersHealth.ariaLabel')}
            data-testid="runners-health-pill"
            data-state={state.color}
          >
            <Badge variant="dot" color={state.color} className="w-2 h-2 shrink-0" />
            <span className="truncate font-550">{label}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="start"
          className="px-3 py-2 max-w-[260px]"
          data-testid="runners-health-tooltip"
        >
          <RunnerTooltipBody runners={runners} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Returns the visual state derived from the current runners list.
// Pure function so it's covered by unit tests directly.
//
// INV-X: capacity is derived from the authoritative usage cache
// (Anthropic OAuth data via S20.8), not from the heuristic
// `cap5hResidualPct` column. When the usage cache has an entry for a
// runner, `100 - session.percentageUsed` replaces the old cap value.
// When the cache row is missing or in error state, the pill treats
// the runner's cap as unknown.
export function derivePillState(
  runners: RunnerSnapshot[],
  usageById: Record<string, RunnerUsageEntry> = {},
): PillState {
  const total = runners.length;

  const online = runners.filter(
    (r) => r.status === 'online' && !r.drainMode && r.unavailableReason === null,
  );

  // Derive worst cap from the authoritative usage cache. For each
  // online runner, read `usage.five_hour.utilization` from the cache.
  // Cap residual = 100 - utilization. Missing/error rows → unknown.
  const capValues = online
    .map((r) => {
      const entry = usageById[r.id];
      if (!entry?.usage?.five_hour) return null;
      return 100 - entry.usage.five_hour.utilization;
    })
    .filter((v): v is number => typeof v === 'number');
  const capWorst = capValues.length > 0 ? Math.min(...capValues) : null;

  if (total === 0 || online.length === 0) {
    return { color: 'error', labelKey: 'allOffline', total, online: 0, capWorst: null };
  }

  const someDegraded = online.length < total;
  const capLow = capWorst !== null && capWorst <= CAP_WARNING_THRESHOLD_PCT;

  if (someDegraded || capLow) {
    return {
      color: 'warning',
      labelKey: someDegraded ? 'degraded' : total === 1 ? 'singleRunner' : 'allHealthy',
      total,
      online: online.length,
      capWorst,
    };
  }

  return {
    color: 'success',
    labelKey: total === 1 ? 'singleRunner' : 'allHealthy',
    total,
    online: online.length,
    capWorst,
  };
}

type TranslateFn = ReturnType<typeof useTranslation>['t'];

function renderLabel(t: TranslateFn, state: PillState): string {
  const capSegment =
    state.capWorst === null
      ? null
      : t('runnersHealth.cap.percent', { value: Math.round(state.capWorst) });

  switch (state.labelKey) {
    case 'allOffline':
      return t('runnersHealth.allOffline');
    case 'singleRunner':
      return capSegment
        ? t('runnersHealth.singleRunnerWithCap', { cap: capSegment })
        : t('runnersHealth.singleRunner');
    case 'degraded':
      return t('runnersHealth.degraded', {
        online: state.online,
        total: state.total,
      });
    case 'allHealthy':
      return capSegment
        ? t('runnersHealth.allHealthyWithCap', {
            count: state.total,
            cap: capSegment,
          })
        : t('runnersHealth.allHealthy', { count: state.total });
  }
}

function RunnerTooltipBody({ runners }: { runners: RunnerSnapshot[] }) {
  const { t } = useTranslation();
  const usageById = useRunnersUsageStore((s) => s.entriesByRunnerId);

  if (runners.length === 0) {
    return <span>{t('runnersHealth.tooltip.empty')}</span>;
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-caption font-550 opacity-80">
        {t('runnersHealth.tooltip.title')}
      </div>
      <div className="flex flex-col gap-1">
        {runners.map((r) => (
          <RunnerTooltipRow key={r.id} runner={r} usageEntry={usageById[r.id]} />
        ))}
      </div>
    </div>
  );
}

function RunnerTooltipRow({
  runner,
  usageEntry,
}: {
  runner: RunnerSnapshot;
  usageEntry?: RunnerUsageEntry;
}) {
  const { t } = useTranslation();
  const label = deriveRunnerLabel(runner.id);
  const isAvailable =
    runner.status === 'online' && !runner.drainMode && runner.unavailableReason === null;
  const dotColor: BadgeColor = isAvailable ? 'success' : 'error';

  const tail = (() => {
    if (!isAvailable) return t('runnersHealth.tooltip.unavailable');
    // INV-X: derive cap from authoritative usage cache
    if (!usageEntry?.usage?.five_hour) {
      return t('runnersHealth.tooltip.capUnknown');
    }
    const residual = 100 - usageEntry.usage.five_hour.utilization;
    return t('runnersHealth.tooltip.capFree', {
      value: Math.round(residual),
    });
  })();

  return (
    <div
      className="flex items-center gap-2 text-caption tabular-nums"
      data-testid={`runners-health-tooltip-row-${runner.id}`}
    >
      <Badge variant="dot" color={dotColor} className="w-1.5 h-1.5 shrink-0" />
      <span className="font-550 min-w-[3ch]">{label}</span>
      <span className="opacity-70">{tail}</span>
    </div>
  );
}
